<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-credit-card"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">Payment Settings</div>
          <div class="panel-header__sub">
            Configure your
            <strong>{{ currentUser.c.payment_method === 'heartland' ? 'Heartland' : 'CardPointe' }}</strong>
            payment gateway credentials
          </div>
        </div>
        <div class="panel-header__badge">
          <span class="method-badge">
            <i :class="currentUser.c.payment_method === 'heartland' ? 'fa fa-heartbeat' : 'fa fa-credit-card'"></i>
            {{ currentUser.c.payment_method === 'heartland' ? 'Heartland' : 'CardPointe' }}
          </span>
        </div>
      </div>

      <!-- CardPointe Form -->
      <div class="form-section" v-if="currentUser.c.payment_method !== 'heartland'">
        <div class="section-title">
          <i class="fa fa-lock section-title__icon"></i>
          CardPointe Gateway Credentials
        </div>

        <div class="field-grid">
          <div class="field-group" :class="{ 'field-group--error': errors['username'] }">
            <label class="field-label">Username <span class="req">*</span></label>
            <input
              v-model="username"
              type="text"
              class="field-input"
              placeholder="Enter username"
              @keydown.space.prevent
            />
            <span class="field-error" v-if="errors['username']">{{ errors['username'][0] }}</span>
          </div>

          <div class="field-group" :class="{ 'field-group--error': errors['password'] }">
            <label class="field-label">Password <span class="req">*</span></label>
            <div class="input-eye">
              <input
                v-model="password"
                :type="password_type"
                class="field-input field-input--eye"
                placeholder="Enter password"
                @keydown.space.prevent
              />
              <button class="eye-btn" type="button" @click="showPassword">
                <i :class="password_type === 'password' ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
              </button>
            </div>
            <span class="field-error" v-if="errors['password']">{{ errors['password'][0] }}</span>
          </div>

          <div class="field-group" :class="{ 'field-group--error': errors['merchant_id'] }">
            <label class="field-label">Merchant ID <span class="req">*</span></label>
            <input
              v-model="merchant_id"
              type="text"
              class="field-input"
              placeholder="Enter merchant ID"
              @keydown.space.prevent
            />
            <span class="field-error" v-if="errors['merchant_id']">{{ errors['merchant_id'][0] }}</span>
          </div>

          <div class="field-group field-group--full" :class="{ 'field-group--error': errors['payment_url'] }">
            <label class="field-label">Payment URL <span class="req">*</span></label>
            <div class="input-icon">
              <i class="fa fa-link input-icon__sym"></i>
              <input
                v-model="payment_url"
                type="text"
                class="field-input field-input--icon"
                placeholder="https://..."
                @keydown.space.prevent
              />
            </div>
            <span class="field-error" v-if="errors['payment_url']">{{ errors['payment_url'][0] }}</span>
          </div>

          <div class="field-group field-group--full" :class="{ 'field-group--error': errors['payment_token_url'] }">
            <label class="field-label">Token URL <span class="req">*</span></label>
            <div class="input-icon">
              <i class="fa fa-link input-icon__sym"></i>
              <input
                v-model="payment_token_url"
                type="text"
                class="field-input field-input--icon"
                placeholder="https://..."
                @keydown.space.prevent
              />
            </div>
            <span class="field-error" v-if="errors['payment_token_url']">{{ errors['payment_token_url'][0] }}</span>
          </div>
        </div>
      </div>

      <!-- Heartland Form -->
      <div class="form-section" v-else>
        <div class="section-title">
          <i class="fa fa-heartbeat section-title__icon"></i>
          Heartland Gateway Credentials
        </div>

        <div class="field-grid">
          <div class="field-group field-group--full" :class="{ 'field-group--error': errors['public_key'] }">
            <label class="field-label">Public Key <span class="req">*</span></label>
            <div class="input-icon">
              <i class="fa fa-key input-icon__sym"></i>
              <input
                v-model="public_key"
                type="text"
                class="field-input field-input--icon"
                placeholder="Enter public key"
                @keydown.space.prevent
              />
            </div>
            <span class="field-error" v-if="errors['public_key']">{{ errors['public_key'][0] }}</span>
          </div>

          <div class="field-group field-group--full" :class="{ 'field-group--error': errors['secret_api_key'] }">
            <label class="field-label">Secret API Key <span class="req">*</span></label>
            <div class="input-icon">
              <i class="fa fa-lock input-icon__sym"></i>
              <input
                v-model="secret_api_key"
                type="text"
                class="field-input field-input--icon"
                placeholder="Enter secret API key"
                @keydown.space.prevent
              />
            </div>
            <span class="field-error" v-if="errors['secret_api_key']">{{ errors['secret_api_key'][0] }}</span>
          </div>

          <div class="field-group" :class="{ 'field-group--error': errors['developer_id'] }">
            <label class="field-label">Developer ID <span class="req">*</span></label>
            <input
              v-model="developer_id"
              type="text"
              class="field-input"
              placeholder="Enter developer ID"
              @keydown.space.prevent
            />
            <span class="field-error" v-if="errors['developer_id']">{{ errors['developer_id'][0] }}</span>
          </div>

          <div class="field-group" :class="{ 'field-group--error': errors['version_number'] }">
            <label class="field-label">Version Number <span class="req">*</span></label>
            <input
              v-model="version_number"
              type="text"
              class="field-input"
              placeholder="e.g. 1.0"
              @keydown.space.prevent
            />
            <span class="field-error" v-if="errors['version_number']">{{ errors['version_number'][0] }}</span>
          </div>

          <div class="field-group field-group--full" :class="{ 'field-group--error': errors['service_url'] }">
            <label class="field-label">Service URL <span class="req">*</span></label>
            <div class="input-icon">
              <i class="fa fa-link input-icon__sym"></i>
              <input
                v-model="service_url"
                type="text"
                class="field-input field-input--icon"
                placeholder="https://..."
                @keydown.space.prevent
              />
            </div>
            <span class="field-error" v-if="errors['service_url']">{{ errors['service_url'][0] }}</span>
          </div>
        </div>
      </div>

    </div>

    <!-- Floating save bar -->
    <div class="save-bar">
      <span class="save-bar__hint">
        <i class="fa fa-info-circle"></i> Credentials are encrypted and stored securely
      </span>
      <button
        v-if="currentUser.c.payment_method !== 'heartland'"
        class="save-bar__save"
        ref="kt_save_payment_setting"
        @click="onSubmit"
      >
        <i class="fa fa-save"></i> Save Settings
      </button>
      <button
        v-else
        class="save-bar__save"
        ref="kt_save_heart_payment_setting"
        @click="onHeartSubmit"
      >
        <i class="fa fa-save"></i> Save Settings
      </button>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";
import {
  FETCH_PAYMENT_SETTING,
  UPDATE_PAYMENT_SETTING,
  FETCH_HEART_PAYMENT_SETTING,
  UPDATE_HEART_PAYMENT_SETTING
} from "@/core/services/store/actions.type";

export default {
  data() {
    return {
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Setting",   route: "" },
        { id: 3, title: "Payment Method Setting", route: "" },
      ],
      errors: [],
      id: 0,
      username: "",
      password: "",
      merchant_id: "",
      password_type: "password",
      payment_url: "",
      payment_token_url: "",
      heart_id: 0,
      public_key: "",
      secret_api_key: "",
      developer_id: "",
      version_number: "",
      service_url: "",
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    if (this.currentUser.c.payment_method !== "heartland") {
      this.$store.dispatch(FETCH_PAYMENT_SETTING).then(data => {
        if (data.status === true) {
          this.id = data.data.id;
          this.username = data.data.username;
          this.password = data.data.password;
          this.merchant_id = data.data.merchant_id;
          this.payment_url = data.data.payment_url;
          this.payment_token_url = data.data.payment_token_url;
        }
      });
    } else {
      this.$store.dispatch(FETCH_HEART_PAYMENT_SETTING).then(data => {
        if (data.status === true) {
          this.heart_id = data.data.id;
          this.public_key = data.data.public_key;
          this.secret_api_key = data.data.secret_api_key;
          this.developer_id = data.data.developer_id;
          this.version_number = data.data.version_number;
          this.service_url = data.data.service_url;
        }
      });
    }
  },
  methods: {
    showPassword() {
      this.password_type = this.password_type === "password" ? "text" : "password";
    },
    onSubmit() {
      this.errors = [];
      const btn = this.$refs["kt_save_payment_setting"];
      btn.disabled = true;
      btn.classList.add("spinner", "spinner-light", "spinner-right");
      this.$store
        .dispatch(UPDATE_PAYMENT_SETTING, {
          id_: this.id,
          merchant_id: this.merchant_id,
          username: this.username,
          password: this.password,
          payment_url: this.payment_url,
          payment_token_url: this.payment_token_url,
        })
        .then(data => {
          btn.classList.remove("spinner", "spinner-light", "spinner-right");
          btn.disabled = false;
          if (this.id === 0) this.id = data.id;
          this.$toastr.s(data.msg);
        })
        .catch(err => {
          this.errors = err.error;
          btn.classList.remove("spinner", "spinner-light", "spinner-right");
          btn.disabled = false;
        });
    },
    onHeartSubmit() {
      this.errors = [];
      const btn = this.$refs["kt_save_heart_payment_setting"];
      btn.disabled = true;
      btn.classList.add("spinner", "spinner-light", "spinner-right");
      this.$store
        .dispatch(UPDATE_HEART_PAYMENT_SETTING, {
          id_: this.heart_id,
          public_key: this.public_key,
          secret_api_key: this.secret_api_key,
          developer_id: this.developer_id,
          version_number: this.version_number,
          service_url: this.service_url,
        })
        .then(data => {
          btn.classList.remove("spinner", "spinner-light", "spinner-right");
          btn.disabled = false;
          this.heart_id = data.id;
          this.$toastr.s(data.msg);
        })
        .catch(err => {
          this.errors = err.error;
          btn.classList.remove("spinner", "spinner-light", "spinner-right");
          btn.disabled = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$border: #dde2ec;

.page-wrap {
  padding: 12px 12px 96px;
  font-family: $font;
}

/* ── panel card ── */
.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: clip;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* ── header ── */
.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px; height: 48px; border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 18px; flex-shrink: 0;
  }

  &__text { flex: 1; }

  &__title {
    font-size: 16px; font-weight: 700; color: $dark; line-height: 1.2;
  }

  &__sub {
    font-size: 12px; color: #7a8499; margin-top: 3px;
    strong { color: $dark; font-weight: 600; }
  }

  &__badge {
    display: flex; align-items: center;
  }
}

.method-badge {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 12px; border-radius: 20px;
  background: rgba(56,154,212,0.1);
  color: $blue; font-size: 12px; font-weight: 600;
  border: 1px solid rgba(56,154,212,0.2);
  i { font-size: 11px; }
}

/* ── form section ── */
.form-section {
  padding: 24px 28px 28px;
}

.section-title {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; font-weight: 700; color: $dark;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f2f8;

  &__icon {
    width: 26px; height: 26px; border-radius: 6px;
    background: rgba(56,154,212,0.1); color: $blue;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; flex-shrink: 0;
  }
}

/* ── fields ── */
.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}

.field-group {
  display: flex; flex-direction: column; gap: 5px;

  &--full { grid-column: 1 / -1; }

  &--error .field-input { border-color: #b42318; }
}

.field-label {
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.04em;
  color: #5a6278; font-family: $font;

  .req { color: #b42318; margin-left: 1px; }
}

.field-input {
  height: 38px;
  border: 1.5px solid $border; border-radius: 8px;
  padding: 0 11px; font-size: 13px;
  color: $dark; font-family: $font;
  outline: none; background: #fff; width: 100%;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus { border-color: $blue; box-shadow: 0 0 0 3px rgba(56,154,212,0.1); }
  &::placeholder { color: #b0bac9; }
  &--icon  { padding-left: 34px; }
  &--eye   { padding-right: 38px; }
}

.field-error { font-size: 11px; color: #b42318; }

/* ── input with icon ── */
.input-icon {
  position: relative;

  &__sym {
    position: absolute; left: 10px; top: 50%;
    transform: translateY(-50%);
    font-size: 12px; color: #a0aab8; pointer-events: none;
  }
}

/* ── password eye ── */
.input-eye {
  position: relative;
}

.eye-btn {
  position: absolute; right: 1px; top: 1px; bottom: 1px;
  width: 36px; border: none; background: transparent;
  color: #a0aab8; cursor: pointer; border-radius: 0 7px 7px 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; transition: background 0.15s, color 0.15s;

  &:hover { background: #f4f6fb; color: $dark; }
}

/* ── save bar ── */
.save-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  left: 50%;
  transform: translateX(-40%);
  background: #1a2340;
  border-radius: 14px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 8px 32px rgba(13,27,62,0.3);
  z-index: 100;

  &__hint {
    display: flex; align-items: center; gap: 7px;
    font-size: 12px; color: rgba(255,255,255,0.55); font-family: $font;
    i { font-size: 12px; }
  }

  &__save {
    display: inline-flex; align-items: center; gap: 7px;
    background: $blue; color: #fff; border: none; border-radius: 9px;
    padding: 9px 22px; font-size: 13px; font-weight: 600;
    font-family: $font; cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;

    &:hover { background: #2d85bb; box-shadow: 0 4px 14px rgba(56,154,212,0.4); }
    &:disabled { opacity: 0.6; cursor: default; box-shadow: none; }

    i { font-size: 12px; }
  }
}
</style>
